/**
 * base64url (no padding) and UTF-8 encoding of the deferred mailbox.
 *
 * It is the SAME alphabet that WebCrypto produces/expects in the rest of the module and
 * that the server decodes (`base64::URL_SAFE_NO_PAD` in `apps/signaling`).
 * Without `Buffer` (this runs in the browser): `btoa`/`atob` by chunks.
 */

const CHUNK = 0x80_00;

export function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

/** Throws `Error` if the text is not valid base64url. */
export function fromBase64Url(text: string): Uint8Array {
  if (!/^[\w-]*$/.test(text)) {
    throw new Error("invalid base64url");
  }
  const padded = text.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function utf8Encode(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

export function utf8Decode(bytes: Uint8Array): string {
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

/** Copies to a fresh `ArrayBuffer` (satisfies `BufferSource` without assertions). */
export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.length);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}
